'use client'

import classNames from 'classnames'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import type { FlatMetaFieldSchema, SellerType } from '@/config/metadata-schemas.config'
import { parseMetaValue } from '@/utils/metadata-schema.utils'
import {
  formatMetaExample,
  isFieldRequired,
  isStructuredBlobSchema,
} from '@/utils/metadata-form.utils'
import { getMetaFieldElementId } from '@/utils/metadata-field-view.utils'
import { humanizeMetaKey } from '@/utils/metadata-label.utils'
import { updateMetadataAction } from '@/app/_actions/metadata-management-tool.actions'
import MetadataBlobEditor from './MetadataBlobEditor'
import styles from './MetadataManagementTool.module.css'

type Props = {
  schema: FlatMetaFieldSchema
  resourceId: string
  initialValue: string
  sellerType: SellerType
  /** Hidden (not unmounted) by the panel's search/filter, so unsaved edits survive. */
  hidden?: boolean
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

const itemLabelForKey = (key: string): string | undefined => {
  if (key === 'slider') {
    return 'Slide'
  }
  if (key === 'pricing' || key === 'exhibitionInformation') {
    return 'Section'
  }
  return undefined
}

/**
 * Render the input for a field by its schema type. All values are held as the
 * Ticketure/Edge-Config WIRE string, so booleans are '1'/'0' and blobs are the
 * raw JSON text — exactly what the write path stores.
 */
const renderInput = (
  id: string,
  schema: FlatMetaFieldSchema,
  value: string,
  onChange: (next: string) => void,
): ReactNode => {
  if (schema.type === 'boolean') {
    return (
      <input
        id={id}
        type="checkbox"
        checked={value === '1' || value === 'true'}
        onChange={(event) => onChange(event.target.checked ? '1' : '0')}
      />
    )
  }

  if (schema.type === 'number') {
    return <input id={id} type="number" value={value} onChange={(event) => onChange(event.target.value)} />
  }

  if (schema.type === 'string' && schema.enum) {
    return (
      <select id={id} value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">—</option>
        {schema.enum.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    )
  }

  // Structured editor for modeled blobs (slider, pricing, exhibitionInformation, …)
  if (isStructuredBlobSchema(schema)) {
    return (
      <MetadataBlobEditor
        id={id}
        schema={schema}
        itemLabel={itemLabelForKey(schema.key)}
        value={value}
        onChange={onChange}
      />
    )
  }

  if (schema.type === 'object' || schema.type === 'array') {
    return <textarea id={id} value={value} onChange={(event) => onChange(event.target.value)} rows={4} />
  }

  return <input id={id} type="text" value={value} onChange={(event) => onChange(event.target.value)} />
}

/**
 * One editable metadata field: an input, live validation against its own schema,
 * the description + example, and a Save button that writes just this field.
 * An optional field may be saved blank (clearing its value); a required field
 * cannot, since a blank is exactly what "missing" means for it. Independent
 * of every other field — "save one record at a time".
 */
const MetadataField = ({ schema, resourceId, initialValue, sellerType, hidden }: Props): ReactNode => {
  const [value, setValue] = useState(initialValue)
  const [status, setStatus] = useState<SaveStatus>('idle')

  const required = isFieldRequired(schema, sellerType)
  const isEmpty = value.trim() === ''

  const validation = useMemo(() => (
    isEmpty ? { valid: true, errors: [] as string[] } : parseMetaValue(schema, value)
  ), [schema, value, isEmpty])

  const isMissing = required && isEmpty
  const isInvalid = !validation.valid
  const inputId = `metadata-input-${schema.resourceType}-${resourceId}-${schema.key}`

  const handleChange = useCallback((next: string) => {
    setValue(next)
    setStatus('idle')
  }, [])

  const handleSave = useCallback(async () => {
    const row: MetadataManagementTool.Metadata = {
      key: schema.key,
      resourceType: schema.resourceType,
      resourceId,
      value,
    }

    setStatus('saving')
    try {
      await updateMetadataAction(resourceId, resourceId, row)
      setStatus('saved')
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[MetadataField] save failed', { key: schema.key, resourceType: schema.resourceType, resourceId, value }, error)
      setStatus('error')
    }
  }, [schema, resourceId, value])

  return (
    <div
      id={getMetaFieldElementId(schema.resourceType, resourceId, schema.key)}
      hidden={hidden}
      className={classNames(styles.Field, {
        [styles.Missing]: isMissing,
        [styles.Invalid]: isInvalid,
      })}
      data-testid={`metadata-field-${schema.key}`}
    >
      <label className={styles.FieldLabel} htmlFor={inputId}>
        {humanizeMetaKey(schema.key)}
        {required && <span className={styles.Required}> *</span>}
      </label>
      {renderInput(inputId, schema, value, handleChange)}
      {/* Help/example stay collapsed until the field needs attention or focus (progressive disclosure via CSS). */}
      <div className={styles.FieldDetails}>
        <p className={styles.Help}>{schema.description}</p>
        <p className={styles.Example}>Example: {formatMetaExample(schema.examples[0])}</p>
      </div>
      {isInvalid && <p className={styles.Error} role="alert">{validation.errors.join(', ')}</p>}
      <div className={styles.FieldActions}>
        <button type="button" className={styles.SaveButton} onClick={handleSave} disabled={status === 'saving' || isInvalid || isMissing}>
          {status === 'saving' ? 'Saving…' : 'Save'}
        </button>
        {status === 'saved' && <span className={styles.StatusSaved}>Saved</span>}
        {status === 'error' && <span className={styles.StatusError}>Save failed</span>}
      </div>
    </div>
  )
}

export default MetadataField
